import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { createStudent, updatePupilPeriod, deleteStudent, getStudent } from '../../services/scpApi';
import { yearGroups, studentStatuses, typesOfIntervention, attendanceIndicators } from '../../data/data';
import { getTutorGroups, getSchools, updatePupilDetails, getDataResource } from '../../services/scpApi';
import { clearAllSelections } from "../../slices/portalSelectedSlice";
import DatePicker from "react-datepicker";
import bin from '../../assets/bin.svg';
import CustomSelect from "../../features/customSelect/CustomSelect";
import SaveButton from "../../features/saveButton/SaveButton";

const EditDetails = () => {

    const [formData, setFormData] = useState({
        studentId: '',
        schoolId: '',
        name: '',
        yearGroup: '',
        tutor: '',
        tutorGroup: '',
        studentStatus: '',
        interventionType: '',
        attendance: '',
        interventionCompletedDate: '01/01/1970',
        partialPeriodCompletion: false
    });
    const [latestPeriod, setLatestPeriod] = useState({});
    const [schools, setSchools] = useState([]);
    const [tutors, setTutors] = useState([]);
    const [tutorGroups, setTutorGroups] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [assessmentSchema, setAssessmentSchema] = useState(null);
    const user = useSelector(state => state.auth.authUser);
    const selectedStudent = useSelector(state => state.portalSelected.selectedStudent);
    const [student, setStudent] = useState({});
    const [loading, setLoading] = useState(null);
    const dispatch = useDispatch();



    useEffect(() => {

        if (!selectedStudent || !selectedStudent.id) {
            setStudent({});
            return;
        }

        (async () => {
            try {
                const studentData = await getStudent(selectedStudent.id);
                setStudent(studentData);

            } catch (error) {
                console.log(error);
            }
        })();

    }, [selectedStudent])


    // prepare form data for editing existing student
    useEffect(() => {
        if (!student) {
            return;
        }

        (async () => {
            try {

                // set form data for updating student
                if (student) {
                    const mostRecentPeriod = student.pupil_periods[student.pupil_periods.length - 1] || {};
                    setLatestPeriod(mostRecentPeriod);
        
                    const newFormData = {
                        studentId: student.id || '',
                        schoolId: student.school.id || '',
                        name: student.name || '',
                        yearGroup: mostRecentPeriod.year_group || '',
                        tutor: mostRecentPeriod.tutor && mostRecentPeriod.tutor.id || '',
                        tutorGroup: mostRecentPeriod.tutor_group && mostRecentPeriod.tutor_group.id || '',
                        studentStatus: mostRecentPeriod.pupil_status || '',
                        interventionType: mostRecentPeriod.intervention_type || '',
                        attendance: mostRecentPeriod.attendance || '',
                        interventionCompletedDate: student.intervention_completed_date || '',
                        partialPeriodCompletion: mostRecentPeriod.partial_period_completion || false
                    };
        
                    setFormData(newFormData);
                }
                
                // set back to default if no student is selected
                else {        
                    setFormData({
                        studentId: '',
                        schoolId: '',
                        name: '',
                        yearGroup: '',
                        tutor: '',
                        tutorGroup: '',
                        studentStatus: '',
                        interventionType: '',
                        attendance: '',                        
                        interventionCompletedDate: '',
                        partialPeriodCompletion: false
                    });
                }

            } catch (error) {
                console.error('Error fetching assessment schema:', error);
            }
        })();

    }, [student]);

    // get schools for select
    useEffect(() => {
        (async () => {
            try {
                const schoolsData = await getSchools();
                setSchools(schoolsData);
            } catch (error) {
                console.error('Error fetching schools:', error);
            }
        })();
    }, []);

    // get assessment schema
    useEffect(() => {
        (async () => {
            try {
                const assessmentSchema = await getDataResource('maths-assessment');
                console.log('assessment schema:', assessmentSchema);

                // set the form data baseline assessment = assessment schema if it doesn't already exist
                if (!formData.baselineAssessment) {
                    handleBaselineChange(assessmentSchema);
                    setAssessmentSchema(assessmentSchema);
                }
                
                // otherwise use the existing baseline assessment as the schema
                else {
                    setAssessmentSchema(student.baseline_assessment);
                }

            } catch (error) {
                console.error('Error fetching assessment schema:', error);
            }
        })();
    }, []);


    // get tutor groups for select
    useEffect(() => {
        if (!formData.schoolId) {
            return;
        }

        (async () => {
            try {
                const tutorGroupsData = await getTutorGroups({
                    schoolId: formData.schoolId
                });

                // prefix tutor group name with tutor name
                tutorGroupsData.forEach(group => {
                    group.name = `${group.tutor.name} - ${group.name}`;
                });

                // sort alphabetically
                tutorGroupsData.sort((a, b) => a.name.localeCompare(b.name));

                setTutorGroups(tutorGroupsData);
            } catch (error) {
                console.error('Error fetching tutor groups:', error);
            }
        })();
    }, [formData.schoolId]);


    // handle form data changes
    const handleChange = (e) => {
        // unset validation errors
        setValidationErrors([]);

        // handle form data change
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // handle baseline assessment change
    const handleBaselineChange = (baselineAssessment) => {
        setFormData(prevData => ({
            ...prevData,
            baselineAssessment
        }));

        console.log('baseline assessment changed:', baselineAssessment);
    };

    // handle delete student
    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this student?')) {
            try {
                setLoading('loading');

                await deleteStudent(student.id);

                setLoading('success');
                setTimeout(() => {
                    setLoading(null);
                }, 3000);

                dispatch(clearAllSelections());
                 

            } catch (error) {

                setLoading('error');
                console.error('Error deleting student:', error);
            }
        } else {
            console.log('Deletion canceled');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidationErrors([]);
        setLoading('loading');

        console.log('completion date:', formData.interventionCompletedDate);
        console.log('PRE SUBMIT DATA: ', formData);
        // return;

        // save student
        try {
            if (student) {
                // Editing existing student
                await updatePupilDetails({
                    studentId: formData.studentId,
                    schoolId: formData.schoolId,
                    name: formData.name,
                    baseline_assessment: formData.baselineAssessment,
                    baseline_assessment_completed_date: formData.baselineAssessmentCompletedDate,
                    intervention_completed_date: formData.interventionCompletedDate,
                    tutorGroupId: formData.tutorGroup,
                    yearGroup: formData.yearGroup,
                    interventionType: formData.interventionType,
                    pupilStatus: formData.studentStatus,
                    attendance: formData.attendance,

                });

                // await updatePupilPeriod({
                //     studentId: student.id,
                //     tutorGroupId: formData.tutorGroup,
                //     yearGroup: formData.yearGroup,
                //     interventionType: formData.interventionType,
                //     pupilStatus: formData.studentStatus,
                //     attendance: formData.attendance,
                //     //partialPeriodCompletion: formData.partialPeriodCompletion
                // });

                setLoading('success');
                setTimeout(() => {
                    setLoading(null);
                }, 3000);


            } else {
                // Creating new student 
                const student = await createStudent({
                    school: formData.schoolId,
                    name: formData.name,
                    baseline_assessment: formData.baselineAssessment,
                    baseline_assessment_completed_date: formData.baselineAssessmentCompletedDate,
                    intervention_completed_date: formData.interventionCompletedDate,
                    tutor_group: formData.tutorGroup,
                    year_group: formData.yearGroup,
                    intervention_type: formData.interventionType,
                    pupil_status: formData.studentStatus,
                    attendance: formData.attendance
                    //partial_period_completion: formData.partialPeriodCompletion
                });

                setLoading('success');
                setTimeout(() => {
                    setLoading(null);
                }, 3000);

            }
        } catch (error) {
            // server side validation
            const errors = [];

            if (error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((key) => {
                    errors.push(error.response.data.errors[key]);
                });
            } else if (error.response.data.message) {
                errors.push(error.response.data.message);
            }
            setValidationErrors(errors);
            console.error('Error saving student:', error);

            setLoading('error');
            setTimeout(() => {
                setLoading(null);
            }, 3000);
        }
    };

    console.log('EDIT DETAILS FORM DATA: ', formData);


    return (
        <div className="single-edit">
            <h2>{student ? 'Edit Student' : 'Add New Student'}</h2>
            {user.user_role === 'super_admin' && (
                <img className='bin-icon' src={bin} alt="rubbish bin icon" onClick={handleDelete} />
            )}
            <form onSubmit={handleSubmit}>
                <div className='single-inner-cont'>
                <label>School:</label>
                <CustomSelect 
                    name="select-school" 
                    placeholder="Select School"
                    options={schools}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'schoolId', value: option.id } })}
                    selectedOption={schools.find(group => group.id === formData.schoolId)}
                />

                <label>Name:</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                />

                <label>Year Group:</label>
                {latestPeriod && latestPeriod.period_name &&
                    <span className="field-instruction">Editing for period: {latestPeriod.period_name} {latestPeriod.calendar_year}</span>
                }
                <CustomSelect 
                    name="select-year-group" 
                    placeholder="Select Year Group"
                    options={yearGroups}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'yearGroup', value: option.year } })}
                    selectedOption={yearGroups.find(yearGroup => yearGroup.year === formData.yearGroup)}
                />

                {/* <label>Tutor:</label>
                {latestPeriod && latestPeriod.period_name &&
                    <span className="field-instruction">Editing for period: {latestPeriod.period_name} {latestPeriod.calendar_year}</span>
                }
                <CustomSelect 
                    name="select-tutor" 
                    placeholder="Select Tutor"
                    options={tutors}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'tutor', value: option.id } })}
                    selectedOption={tutors.find(tutor => tutor.id === formData.tutor)}
                /> */}

                <label>Tutor Group:</label>
                {latestPeriod && latestPeriod.period_name &&
                    <span className="field-instruction">Editing for period: {latestPeriod.period_name} {latestPeriod.calendar_year}</span>
                }
                <CustomSelect 
                    name="select-tutor-group" 
                    placeholder="Select Tutor Group"
                    options={tutorGroups}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'tutorGroup', value: option.id } })}
                    selectedOption={tutorGroups.find(tutorGroup => tutorGroup.id === formData.tutorGroup)}
                />

                <label>Intervention Type:</label>
                {latestPeriod && latestPeriod.period_name &&
                    <span className="field-instruction">Editing for period: {latestPeriod.period_name} {latestPeriod.calendar_year}</span>
                }
                <CustomSelect 
                    name="intervention-type" 
                    placeholder="Select type"
                    options={typesOfIntervention}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'interventionType', value: option.type } })}
                    selectedOption={typesOfIntervention.find(option => option.type === formData.interventionType)}
                />

                <label>Student Status:</label>
                {latestPeriod && latestPeriod.period_name &&
                    <span className="field-instruction">Editing for period: {latestPeriod.period_name} {latestPeriod.calendar_year}</span>
                }
                <CustomSelect 
                    name="student-status" 
                    placeholder="Select status"
                    options={studentStatuses}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'studentStatus', value: option.status } })}
                    selectedOption={studentStatuses.find(status => status.status === formData.studentStatus)}
                />

                <label>Intervention Completed Date:</label>
                <span className="field-instruction">Leave empty if student not yet completed intervention</span>
                <div className="date-selector-cont">
                    <DatePicker
                        selected={formData.interventionCompletedDate ? new Date(formData.interventionCompletedDate) : new Date('01/01/1970')}
                        onChange={(date) => handleChange({ target: { name: 'interventionCompletedDate', value: date } })}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select Date"
                    />
                </div>

                <label>Attendance Indicator:</label>
                {latestPeriod && latestPeriod.period_name &&
                    <span className="field-instruction">Editing for period: {latestPeriod.period_name} {latestPeriod.calendar_year}</span>
                }
                <CustomSelect
                    name="attendance" 
                    placeholder="Select status"
                    options={attendanceIndicators}
                    labelKey="name"
                    setSelectedOption={(option) => handleChange({ target: { name: 'attendance', value: option.status } })}
                    selectedOption={attendanceIndicators.find(status => status.status === formData.attendance)}
                />

                <div className="flex-row-buttons-cont">
                    <SaveButton
                        save={handleSubmit}
                        loading={loading}
                        textCreate="Add Student"
                        textCreateLoading="Saving..."
                        textCreateSuccess="Saved"
                        textCreateError="Error"
                        textUpdate="Update Student"
                        textUpdateLoading="Updating..."
                        textUpdateSuccess="Updated"
                        textUpdateError="Error"
                        isUpdating={!!student} 
                    />
                </div>

                {validationErrors.length > 0 &&
                    <div className="error-messages">
                        {validationErrors.map((error, index) => {
                            return <p key={index}>{error}</p>
                        })}
                    </div>
                }
                </div>
            </form>
        </div>
    );
}

export default EditDetails;